import Bugsnag from '@bugsnag/js';
import type { FlagsVariant, Mixpanel } from 'mixpanel-browser';

import type { FEATURE } from '~/common/enums/feature.enum';
import log, { logError } from '~/common/utils/log';

import { FLAGS_READY_TIMEOUT_MS } from '../featureFlagSource';

import {
  isSettledStatus,
  type MixpanelFlagsStatus,
  useMixpanelFlagsStore,
} from './state/mixpanelFlags.store';
import {
  adoptAnonymousId,
  type FlagIdentity,
  type IdentitySession,
  markFetched,
  newSession,
  sameVariants,
  switchToAnonymous,
  switchToUser,
  toContext,
} from './mixpanelClient.utils';

export const FLAGS_REFRESH_MS = 5 * 60_000;

const FIRST_PARTY_PROXY_PATH = '/api/mp';

const SLASHLESS_API_ROUTES = {
  track: 'track',
  engage: 'engage',
  groups: 'groups',
  record: 'record',
  flags: 'flags',
  settings: 'settings',
};

class MixpanelFlagsClient {
  private sdk: Mixpanel | null = null;

  private started = false;

  private startedAt = 0;

  private latestIdentity: FlagIdentity | null = null;

  private session: IdentitySession | null = null;

  private readyTimeout: ReturnType<typeof setTimeout> | undefined;

  start = (identity: FlagIdentity): void => {
    this.latestIdentity = identity;
    if (this.started) return;

    const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
    if (!token) {
      this.publish('failed');
      return;
    }

    this.started = true;
    this.startedAt = Date.now();
    this.init(token);
  };

  setIdentity = async (identity: FlagIdentity): Promise<void> => {
    this.latestIdentity = identity;
    const { sdk } = this;
    const context = toContext(identity);

    if (!sdk || context.distinct_id === this.session?.distinctId) return;

    const session = newSession(context.distinct_id, true);
    this.session = session;
    this.armReadyTimeout();

    if (identity.userId) {
      switchToUser(sdk, context, identity.userId);
    } else {
      switchToAnonymous(sdk, context);
      this.publish('loading');
    }

    await this.settleOnFetch(sdk, session);
  };

  trackExposure = (flag: FEATURE): void => {
    const { sdk, session } = this;
    if (!sdk || !session || session.exposed.has(flag)) return;
    if (session.fetchPending) {
      session.deferredExposures.add(flag);
      return;
    }

    const variant = sdk.flags.get_variant_sync(flag, { key: '', value: null });
    if (variant.variant_source !== 'fallback') session.exposed.add(flag);
  };

  private readVariants(): Record<string, FlagsVariant> {
    return Object.fromEntries(this.sdk?.flags.get_all_variants_sync() ?? new Map());
  }

  private publish(status: MixpanelFlagsStatus) {
    const { status: previous, variants: current } = useMixpanelFlagsStore.getState();
    const variants = isSettledStatus(status) ? this.readVariants() : {};

    const isUnchangedPoll = status === previous && sameVariants(variants, current);
    if (isUnchangedPoll) return;
    useMixpanelFlagsStore.getState().publish(status, variants);

    if (this.started && status !== previous && isSettledStatus(status) && Bugsnag.isStarted()) {
      Bugsnag.leaveBreadcrumb('feature-flags settled', {
        provider: 'mixpanel',
        status,
        ms: Date.now() - this.startedAt,
      });
    }
  }

  // Evaluations skipped while the identity fetch was pending are exposed against the new variants.
  private completeFetch(session: IdentitySession) {
    markFetched(session);
    this.publish('ready');

    const variants = this.readVariants();
    session.deferredExposures.forEach((flag) => {
      if (variants[flag]?.is_experiment_active) this.trackExposure(flag);
    });
    session.deferredExposures.clear();
  }

  private statusOnFailure(): MixpanelFlagsStatus {
    if (this.session?.hasNetworkDataForIdentity) return 'ready';

    return Object.keys(this.readVariants()).length ? 'cache' : 'failed';
  }

  private async settleOnFetch(sdk: Mixpanel, session: IdentitySession) {
    try {
      await sdk.flags.when_ready();
      if (session !== this.session) return;

      this.completeFetch(session);
    } catch (error) {
      if (session !== this.session) return;

      log('[feature-flags] Mixpanel flags fetch failed', error);
      session.fetchPending = false;
      session.deferredExposures.clear();
      this.publish(this.statusOnFailure());
    }
  }

  /**
   * get_all_variants() resolves from a valid cache straight away, otherwise after the first fetch.
   * The SDK registers its fetch before this resolves, so when_ready() then waits on that real fetch.
   */
  private async loadInitial(sdk: Mixpanel, session: IdentitySession) {
    const variants = await sdk.flags.get_all_variants();
    if (session !== this.session) return;

    if (Array.from(variants.values()).some(({ variant_source }) => variant_source === 'network')) {
      this.completeFetch(session);
      return;
    }

    if (variants.size > 0) this.publish('cache');
    await this.settleOnFetch(sdk, session);
  }

  private async refresh() {
    const { sdk, session } = this;
    if (!sdk || !session) return;

    try {
      await sdk.flags.load_flags();
      if (session !== this.session) return;

      this.completeFetch(session);
    } catch (error) {
      if (session !== this.session) return;

      log('[feature-flags] Mixpanel flags refresh failed', error);
      this.publish(this.statusOnFailure());
    }
  }

  // Not cleared on settle: a timer that fires once flags have settled does nothing.
  private armReadyTimeout() {
    clearTimeout(this.readyTimeout);
    this.readyTimeout = setTimeout(() => {
      if (!isSettledStatus(useMixpanelFlagsStore.getState().status)) {
        this.publish(this.statusOnFailure());
      }
    }, FLAGS_READY_TIMEOUT_MS);
  }

  private startPolling() {
    setInterval(() => {
      if (document.visibilityState === 'visible') this.refresh();
    }, FLAGS_REFRESH_MS);
    document.addEventListener('visibilitychange', () => {
      const lastFetchAt = this.session?.lastFetchAt ?? 0;
      if (document.visibilityState === 'visible' && Date.now() - lastFetchAt >= FLAGS_REFRESH_MS) {
        this.refresh();
      }
    });
  }

  private async init(token: string) {
    this.publish('loading');
    this.armReadyTimeout();

    try {
      const { default: sdk } = await import('mixpanel-browser/dist/mixpanel-core.cjs');
      // Read after the import so an identity change made while it loaded is picked up.
      const identity = this.latestIdentity;
      if (!identity) throw new Error('Mixpanel flags started without an identity');
      const context = toContext(identity);

      sdk.init(token, {
        api_host: `${window.location.origin}${FIRST_PARTY_PROXY_PATH}`,
        api_routes: SLASHLESS_API_ROUTES,
        ip: false,
        track_pageview: false,
        autocapture: false,
        persistence: 'localStorage',
        debug: process.env.NEXT_PUBLIC_MIXPANEL_DEBUG === 'true',
        flags: {
          context,
          persistence: {
            variantLookupPolicy: 'persistenceUntilNetworkSuccess',
            persistenceTtlMs: FLAGS_REFRESH_MS,
          },
        },
      });
      const session = newSession(context.distinct_id, false);
      this.sdk = sdk;
      this.session = session;
      adoptAnonymousId(sdk, context.device_id, identity.userId);
      if (identity.userId) sdk.identify(identity.userId);
      // Before the initial load, so a first fetch (or IndexedDB open) that hangs still recovers via the poll.
      this.startPolling();

      await this.loadInitial(sdk, session);
    } catch (error) {
      logError('[feature-flags] Mixpanel flags failed to start', error);
      this.publish('failed');
    }
  }
}

export default new MixpanelFlagsClient();
